import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy,
  Pause,
  PlayArrow,
  StopCircle,
  WarningAmber,
} from '@mui/icons-material';
import moment from 'moment';
import { aiModels, modelMapping } from 'data';
import { ALWAYS_ON_TOOLS } from './ToolsPicker';

export const SIMULATIONS_PATH =
  '/f550aeea-0bb4-41e9-b615-7a6cf101e740/simulations';

export const simulationDetailPath = (simulationId) =>
  `${SIMULATIONS_PATH}/${encodeURIComponent(simulationId)}`;

export const STATUS_COLORS = {
  bootstrapping: 'info',
  running: 'success',
  paused: 'warning',
  completed: 'default',
  terminated: 'error',
};

export const CopyableId = ({ value, onCopied }) => (
  <Box
    display={'flex'}
    alignItems={'center'}
    gap={0.5}
    sx={{ whiteSpace: 'nowrap' }}
  >
    <Typography variant={'body2'} fontFamily={'monospace'} fontSize={12}>
      {value || '--'}
    </Typography>
    {value ? (
      <Tooltip title="Copy">
        <ContentCopy
          fontSize="small"
          sx={{ cursor: 'pointer', color: 'text.secondary', width: 14 }}
          onClick={(e) => {
            // Rows are clickable (open detail) — copying must not navigate.
            e.stopPropagation();
            navigator.clipboard.writeText(value);
            if (typeof onCopied === 'function') onCopied();
          }}
        />
      </Tooltip>
    ) : null}
  </Box>
);

export const formatTime = (epochMs) =>
  epochMs ? (
    <Tooltip title={moment(epochMs).format('DD MMMM YYYY, hh:mm:ss A')}>
      <Typography variant={'body2'}>{moment(epochMs).fromNow()}</Typography>
    </Tooltip>
  ) : (
    <Typography variant={'body2'}>--</Typography>
  );

// liveStatus (Requirement 2, Phase 3) is a second, independent signal from the heartbeat
// ai-service sends — complementary to `stale` (activity-based: no tick for 2 intervals), not a
// replacement for it. Only its two anomalous values get a chip here; `live`/`stopped` are the
// expected, healthy states and would just be noise alongside the status chip already shown.
const LIVE_STATUS_CHIP = {
  orphaned: {
    color: 'error',
    label: 'orphaned',
    tooltip:
      'Status says this simulation stopped, but ai-service is still sending a heartbeat for it — its goroutine likely never actually stopped.',
  },
  stale: {
    color: 'warning',
    label: 'no heartbeat',
    tooltip:
      'Status says this simulation is running, but no heartbeat has arrived recently — ai-service may have restarted and lost it.',
  },
};

export const StatusChips = ({ status, stale, liveStatus }) => {
  const liveChip = LIVE_STATUS_CHIP[liveStatus];
  return (
    <Box display={'flex'} alignItems={'center'} gap={0.5}>
      <Chip
        size="small"
        label={status || 'unknown'}
        color={STATUS_COLORS[status] || 'default'}
        variant="outlined"
      />
      {stale ? (
        <Tooltip title="Marked running, but no agent activity for over 2 tick intervals. It has likely stopped — pause and investigate.">
          <Chip
            size="small"
            color="error"
            icon={<WarningAmber fontSize="small" />}
            label="stale"
          />
        </Tooltip>
      ) : null}
      {liveChip ? (
        <Tooltip title={liveChip.tooltip}>
          <Chip
            size="small"
            color={liveChip.color}
            icon={<WarningAmber fontSize="small" />}
            label={liveChip.label}
          />
        </Tooltip>
      ) : null}
    </Box>
  );
};

export const TERMINAL_STATUSES = ['completed', 'terminated'];

export const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(status);

// Pause is only meaningful from `running`; retrigger is only valid from
// `paused` (any other status is a 400); terminate works from any non-terminal
// status and is permanent, so it asks for confirmation first.
export const LifecycleButtons = ({ status, updating, onAction }) => {
  const [confirmTerminate, setConfirmTerminate] = useState(false);

  return (
    <>
      {updating ? (
        <CircularProgress size={18} />
      ) : (
        <Box display={'flex'} justifyContent={'center'} gap={1}>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<Pause fontSize="small" />}
            disabled={status !== 'running'}
            onClick={(e) => {
              e.stopPropagation();
              onAction('pause');
            }}
          >
            Pause
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<PlayArrow fontSize="small" />}
            disabled={status !== 'paused'}
            onClick={(e) => {
              e.stopPropagation();
              onAction('retrigger');
            }}
          >
            Retrigger
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<StopCircle fontSize="small" />}
            disabled={!status || isTerminalStatus(status)}
            onClick={(e) => {
              e.stopPropagation();
              setConfirmTerminate(true);
            }}
          >
            Terminate
          </Button>
        </Box>
      )}
      <Dialog
        open={confirmTerminate}
        onClose={() => setConfirmTerminate(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Terminate simulation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is permanent — unlike pause, a terminated simulation cannot be
            resumed. All agents stop and it stays read-only.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmTerminate(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmTerminate(false);
              onAction('terminate');
            }}
          >
            Terminate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const LIFECYCLE_PAST_TENSE = {
  pause: 'paused',
  retrigger: 'resumed',
  terminate: 'terminated',
};

// A 200 from a lifecycle call only means competition-service changed state;
// the ai-service call that actually starts/stops the agents may still have
// failed.
export const lifecycleAlert = (action, data) => {
  if (action === 'pause' && data?.alreadyPaused) {
    return { severity: 'info', message: 'Simulation was already paused' };
  }
  if (action === 'terminate' && data?.alreadyTerminated) {
    return { severity: 'info', message: 'Simulation had already finished' };
  }
  const aiServiceOk =
    action === 'retrigger'
      ? data?.aiServiceStartTriggered !== false
      : data?.aiServiceStopTriggered !== false;
  if (!aiServiceOk) {
    return {
      severity: 'warning',
      message: `Simulation ${
        LIFECYCLE_PAST_TENSE[action]
      }, but the AI service call failed: ${data?.aiServiceError ||
        'unknown error'}`,
    };
  }
  return {
    severity: 'success',
    message: `Simulation ${LIFECYCLE_PAST_TENSE[action]}`,
  };
};

export const DetailRow = ({ label, children }) => (
  <Box
    display={'flex'}
    flexDirection={{ xs: 'column', md: 'row' }}
    gap={{ xs: 0.5, md: 2 }}
    py={1}
    borderBottom={'1px solid'}
    borderColor={'divider'}
  >
    <Typography
      variant={'body2'}
      color={'text.secondary'}
      sx={{ minWidth: 180 }}
    >
      {label}
    </Typography>
    <Box flex={1} sx={{ wordBreak: 'break-word' }}>
      {children}
    </Box>
  </Box>
);

// `null`/empty means unrestricted; the setup tools are always included.
export const EnabledToolsChips = ({ tools }) => (
  <>
    {!tools || tools.length === 0 ? (
      <Typography variant={'body2'}>All tools (unrestricted)</Typography>
    ) : (
      <Box display={'flex'} flexWrap={'wrap'} gap={0.5}>
        {tools.map((name) =>
          ALWAYS_ON_TOOLS.includes(name) ? (
            <Tooltip key={name} title="Always included — setup tool">
              <Chip size="small" label={name} />
            </Tooltip>
          ) : (
            <Chip key={name} size="small" label={name} variant="outlined" />
          ),
        )}
      </Box>
    )}
    <Typography
      variant={'caption'}
      color={'text.secondary'}
      display={'block'}
      mt={0.5}
    >
      Fixed at creation — tools can’t be changed on an existing simulation.
    </Typography>
  </>
);

// Simulated-day speed: the backend takes `realMsPerSimDay` (real ms of active
// running per simulated calendar day) and defaults to 5 minutes when omitted.
// Admins pick minutes; convert with `minutesToMs` before sending.
export const DEFAULT_SIM_DAY_MINUTES = 5;
export const SIM_DAY_MINUTE_OPTIONS = [1, 2, 3, 5, 10, 15, 30, 60, 120];

export const minutesToMs = (minutes) => Math.round(Number(minutes) * 60000);

// Records created before the field existed don't carry it — they run at the
// backend default.
export const formatSimDaySpeed = (ms) => {
  if (!ms || ms <= 0) return `${DEFAULT_SIM_DAY_MINUTES} min / day (default)`;
  if (ms < 60000) return `${Math.round(ms / 1000)} s / day`;
  const minutes = ms / 60000;
  return `${
    Number.isInteger(minutes) ? minutes : minutes.toFixed(1)
  } min / day`;
};

// Requirement 4, Phase 6 (AI deal simulation plan) — "deal start date" input, defaulting to 180
// days (~6 months) before today, matching the backend default
// (Simulation.DEFAULT_SIMULATED_START_OFFSET_DAYS).
export const DEFAULT_SIMULATED_START_OFFSET_DAYS = 180;

const SIM_DAY_MS = 24 * 60 * 60 * 1000;

// Elapsed simulated time since simulatedStartOn, as "N days (M months, W weeks, D days)" — e.g.
// backend values of currentSimulatedTimeMs=simulatedStartOn+60min-worth-of-ticks at 5 real
// minutes/simulated day reads as "12 days (1 week, 5 days)". Both timestamps are simulated-clock
// values from admin-service's SimulationStatusResponse (Phase 6) — currentSimulatedTimeMs is
// computed fresh server-side on every read, never persisted.
export const formatSimulatedElapsed = (currentSimulatedTimeMs, simulatedStartOn) => {
  if (!simulatedStartOn || !currentSimulatedTimeMs) return null;
  const elapsedMs = Math.max(0, currentSimulatedTimeMs - simulatedStartOn);
  const totalDays = Math.floor(elapsedMs / SIM_DAY_MS);
  const months = Math.floor(totalDays / 30);
  const afterMonths = totalDays % 30;
  const weeks = Math.floor(afterMonths / 7);
  const days = afterMonths % 7;

  const parts = [];
  if (months > 0) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  if (weeks > 0) parts.push(`${weeks} week${weeks === 1 ? '' : 's'}`);
  if (days > 0 || parts.length === 0)
    parts.push(`${days} day${days === 1 ? '' : 's'}`);

  return {
    totalDays,
    breakdown: parts.join(', '),
    label: `${totalDays} day${totalDays === 1 ? '' : 's'} (${parts.join(', ')})`,
  };
};

// LLM provider/model selection — the only 3 providers ai-service's
// BuildCompleter (llm.go) recognizes; an unrecognized provider string is
// rejected by admin-service's SimulationSetupValidator before it ever
// reaches competition-service. MiniMax is the backend default when omitted.
export const LLM_PROVIDERS = ['minimax', 'openai', 'claude'];
export const DEFAULT_LLM_PROVIDER = 'minimax';
export const DEFAULT_LLM_MODEL = 'MiniMax-M3';

const LLM_PROVIDER_LABELS = {
  minimax: 'MiniMax',
  openai: 'OpenAI',
  claude: 'Claude',
};

export const llmProviderLabel = (provider) =>
  LLM_PROVIDER_LABELS[provider] || provider;

// This app's model catalog (data/index.js) isn't scoped to simulations —
// it also lists non-chat/non-LLM entries (SONAR_*, N8N, local Ollama
// models, ...) that ai-service has no provider for. Each simulation
// provider's models are exactly the aiModels keys with the matching
// prefix, reusing the one existing catalog rather than maintaining a
// second model list just for this dropdown.
const LLM_PROVIDER_KEY_PREFIXES = {
  minimax: 'MINIMAX_',
  openai: 'GPT_',
  claude: 'CLAUDE_',
};

// Cascading model options for a selected provider: [{ key, value }], where
// `value` (e.g. "MiniMax-M3") is the real model identifier sent to the
// backend and `key` (e.g. "MINIMAX_M3") is only used as a display label.
export const modelOptionsForProvider = (provider) => {
  const prefix = LLM_PROVIDER_KEY_PREFIXES[provider];
  if (!prefix) return [];
  return aiModels
    .filter((key) => key.startsWith(prefix))
    .map((key) => ({ key, value: modelMapping[key] }));
};

// Placeholders the backend substitutes in a tick-prompt template (see
// renderTickPrompt in ai-service's context.go). Mirrored here only for display.
export const TICK_PROMPT_PLACEHOLDERS = [
  { token: '{{tick_number}}', meaning: 'current tick number' },
  { token: '{{seller_name}}', meaning: 'seller display name' },
  { token: '{{seller_email}}', meaning: 'seller email' },
  { token: '{{seller_company}}', meaning: 'seller company' },
  { token: '{{buyer_name}}', meaning: 'buyer name (buyers: their own; seller: all buyers)' },
  { token: '{{buyer_email}}', meaning: 'buyer email (same rule as buyer name)' },
  { token: '{{buyer_company}}', meaning: 'buyer company' },
];

export const TICK_PROMPT_PLACEHOLDER_HELP = TICK_PROMPT_PLACEHOLDERS.map(
  (p) => p.token,
).join('  ');

// Suggested starter text for the create-dialog's tick-prompt fields — these
// are UI-only defaults (what gets prefilled and, unless edited, sent as an
// explicit template), separate from ai-service's own internal
// DefaultSellerTickPromptTemplate/DefaultBuyerTickPromptTemplate fallback-
// when-blank text. Deliberately buyer/seller-aware from the start, since a
// blank field gave the admin no visibility into who the agent is actually
// addressing.
export const DEFAULT_SELLER_TICK_PROMPT_TEMPLATE =
  "Tick #{{tick_number}}. Check your pending action items and the deal's current stage, then take the single most stage-appropriate action right now. If there is no history of outreach or interaction with any buyer stakeholder yet, initiating contact (e.g. sending an introductory email) is normally the correct stage-appropriate action — \"no action items\" does not by itself mean there is nothing to do. Only do nothing this tick if you have already taken the appropriate action and are genuinely waiting on the other side to respond. You represent {{seller_company}}, selling to {{buyer_name}} at {{buyer_company}}.";

export const DEFAULT_BUYER_TICK_PROMPT_TEMPLATE =
  'Tick #{{tick_number}}. Check for anything addressed to you and decide whether — and how — to respond, in character, given your persona. You are {{buyer_name}} at {{buyer_company}}, being approached by {{seller_name}} from {{seller_company}}.';

// Client-side mirror of ai-service's renderTickPrompt substitution (context.go)
// — used only to render a live, read-only preview in the create dialog, using
// whatever buyer/seller info the admin has already entered elsewhere in the
// same form. tickNumber defaults to 1 as a representative example; the real
// value is resolved per-tick on the backend.
export const renderTickPromptPreview = (template, values) => {
  const {
    sellerName = '',
    sellerCompany = '',
    buyerNames = '',
    buyerCompany = '',
    tickNumber = 1,
  } = values || {};
  const replacements = {
    '{{tick_number}}': String(tickNumber),
    '{{seller_name}}': sellerName,
    '{{seller_company}}': sellerCompany,
    '{{buyer_name}}': buyerNames,
    '{{buyer_company}}': buyerCompany,
  };
  return Object.entries(replacements).reduce(
    (text, [token, value]) => text.split(token).join(value || `(${token})`),
    template || '',
  );
};
